import { Component, OnInit, TemplateRef } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { forkJoin } from "rxjs";

import { CapacitacionProgramacionService } from "../../../../../../../shared/services/capacitaciones_programacion.service";
import { CapacitacionView, CapacitacionesService } from "../../../../../../../shared/services/capacitacion.service";
import { CapacitacionAsistenciaView, CapacitacionesAsistentesService } from "../../../../../../../shared/services/capacitacion_asistentes.service";
import { CapacitacionInvitadosService } from "../../../../../../../shared/services/capacitaciones_invitados.service";
import { PropietarioService } from "../../../../../../../shared/services/propietarios.service";
import { CapacitacionActividadService } from "../../../../../../../shared/services/capacitacion-actividad.service";
import { Utils } from "../../../../../../../shared/utils/Utils";

declare var require: any;
const Swal = require("sweetalert2");

interface AsistenciaRow extends CapacitacionAsistenciaView {
  estado?: number;
}

// 🔹 Un item por CADA TEMA (CapacitacionActividadRe), con los datos de su
// actividad padre (CapacitacionActividad) ya incluidos para mostrar contexto.
interface TemaProgramacion {
  id: number; // id del TEMA (CapacitacionActividadRe.id) -> lo que se guarda como actividad_id en asistencia
  actividad: string; // nombre del tema, ej. "EXTINTOR"
  id_actividad: number; // id de CapacitacionActividad (la sesión)
  fecha_programacion: string;
  hora_inicio: string;
  hora_fin: string;
  dirigido_nombre: string;
  responsable_nombre: string;
  grupoLabel: string; // etiqueta de agrupación en el select, ej. "26/08/2026 · 05:30 PM - 07:30 PM · CONDUCTOR"
}

@Component({
  selector: "app-asistentes",
  templateUrl: "./asistentes.component.html",
  styleUrls: ["./asistentes.component.scss"],
})
export class AsistentesComponent implements OnInit {
  loading = false;
  pageSize = 5;
  page = 1;

  modalAsistentesTab: "asistentes" | "invitados" = "invitados";

  selectedProgramacionId: number | null = null;
  selectedProgramacionCapacitacionId: number | null = null;
  selectedProgramacionCapacitacionNombre: string | null = null;

  invitadosProgramacion: any[] = [];
  loadingInvitados = false;

  asistentes: any[] = [];
  filteredTableItemsAsistencia: any[] = [];

  capacitaciones: CapacitacionView[] = [];

  // ── Temas/actividades de la programación (para que el profesor elija a cuál asistió) ──
  temasProgramacion: TemaProgramacion[] = [];

  // ── Modales ───────────────────────────────────────────────
  editAsistenciaModalRef?: NgbModalRef;
  createModalRef?: NgbModalRef;
  agregarInvitadosModalRef?: NgbModalRef;

  editAsistenciaId: number | null = null;
  editAsistenciaForm: AsistenciaRow = this.formVacioAsistencia();
  newAsistenciaForm: AsistenciaRow = this.formVacioAsistencia();

  // ── Invitados ────────────────────────────────────────────
  invitadosSeleccionados: any[] = [];
  cedulaBuscada: string | number | null = null;
  buscandoInvitado = false;
  personaNoEncontrada = false;
  nuevoInvitadoManual: { cedula: string | number | null; nombre: string } = {
    cedula: null,
    nombre: "",
  };
  selectedProgramacionCapacitacionRefId: number | null = null; 

  modoEdicion = false;
  asistenciaForm: AsistenciaRow = this.formVacioAsistencia();
  asistenciaIdParaEditar: number | null = null;
  editError: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private programacionService: CapacitacionProgramacionService,
    private asistenciaService: CapacitacionesAsistentesService,
    private capacitacionesService: CapacitacionesService,
    private capacitacionInvitadosService: CapacitacionInvitadosService,
    private propietarioService: PropietarioService,
    private actividadService: CapacitacionActividadService,
  ) {}

  ngOnInit(): void {
    this.cargarCapacitaciones();

    this.route.paramMap.subscribe((params) => {
      const id = params.get("id");
      if (id) {
        this.selectedProgramacionId = Number(id);
        this.cargarProgramacionYAsistentes(this.selectedProgramacionId);
        this.cargarInvitadosProgramacion(this.selectedProgramacionId);
        this.cargarTemasProgramacion(this.selectedProgramacionId);
      }
    });
  }

  volver(): void {
    this.router.navigate(["/capacitacion-programacion/capacitacion-programacion"]);
  }

  private formVacioAsistencia(): AsistenciaRow {
    return {
      capacitacion_id: undefined,
      actividad_id: undefined,
      cedula: undefined,
      nombre: "",
      asistio: 1,
      aprobado: 0,
      calificacion: null,
      observaciones: "",
      estado: 1,
    };
  }

  get capacitacionesFiltradas(): CapacitacionView[] {
  if (!this.selectedProgramacionCapacitacionRefId) {
    return this.capacitaciones; // por seguridad, si no hay filtro, muestra todas
  }
  return this.capacitaciones.filter(
    (c) => Number(c.id) === this.selectedProgramacionCapacitacionRefId
  );
}

  cargarCapacitaciones(): void {
    this.capacitacionesService.getAllCapacitacionesActivos().subscribe({
      next: (data) => {
        this.capacitaciones = (data ?? []).filter((c: CapacitacionView) => Number(c.estado ?? 1) !== 0);
      },
      error: () => this.mostrarAlerta("Error", "No se pudieron cargar las capacitaciones", "error"),
    });
  }

  // ── Temas/actividades específicas de la programación ──────────────
  // idProgramacion aquí corresponde al mismo id que usa ActividadesComponent
  // como "id_capacitacion" (CapacitacionActividad.belongsTo(CapacitacionProgramacion)).
  cargarTemasProgramacion(idProgramacion: number): void {
    this.actividadService.getByIdCapacitacion(idProgramacion).subscribe({
      next: (actividades: any[]) => {
        const temas: TemaProgramacion[] = [];

        (actividades ?? [])
          .filter((act: any) => Number(act.estado ?? 1) === 1)
          .forEach((act: any) => {
            const responsableNombre =
              act.nombre_responsable ??
              [
                act.PropietarioCapacitacionData?.primernombre,
                act.PropietarioCapacitacionData?.segundonombre,
                act.PropietarioCapacitacionData?.papellidos,
                act.PropietarioCapacitacionData?.seapellidos,
              ]
                .map((p: string) => (p ?? "").trim())
                .filter((p: string) => !!p)
                .join(" ");

            const dirigidoNombre = act.CargosData?.cargos ?? "-";
            const grupoLabel = `${this.formatearFechaCorta(act.fecha_programacion)} · ${act.hora_inicio} - ${act.hora_fin} · ${dirigidoNombre}`;

            (act.CapacitacionActividadReData ?? [])
              .filter((t: any) => Number(t.estado ?? 1) === 1)
              .forEach((t: any) => {
                temas.push({
                  id: Number(t.id),
                  actividad: t.actividad,
                  id_actividad: Number(act.id),
                  fecha_programacion: act.fecha_programacion,
                  hora_inicio: act.hora_inicio,
                  hora_fin: act.hora_fin,
                  dirigido_nombre: dirigidoNombre,
                  responsable_nombre: responsableNombre || "-",
                  grupoLabel,
                });
              });
          });

        this.temasProgramacion = temas;
      },
      error: () =>
        this.mostrarAlerta(
          "Error",
          "No se pudieron cargar los temas/actividades de la programación",
          "error",
        ),
    });
  }

  formatearFechaCorta(fecha: string): string {
    if (!fecha) return "-";
    const parteFecha = String(fecha).trim().split(/[ T]/)[0];
    const [anio, mes, dia] = parteFecha.split("-");
    return anio && mes && dia ? `${dia}/${mes}/${anio}` : parteFecha;
  }

  obtenerTemaSeleccionado(temaId?: number | null): TemaProgramacion | undefined {
    if (!temaId) return undefined;
    return this.temasProgramacion.find((t) => Number(t.id) === Number(temaId));
  }

  obtenerNombreActividad(temaId?: number | null): string {
    return this.obtenerTemaSeleccionado(temaId)?.actividad ?? "-";
  }

cargarProgramacionYAsistentes(id: number): void {
  this.loading = true;
  this.programacionService.getCapacitacionProgramacionByIdAsistentes(id).subscribe({
    next: (data) => {
      this.loading = false;

      this.selectedProgramacionCapacitacionId = Number(data.id);                 // id de programación
      this.selectedProgramacionCapacitacionRefId = Number(data.capacitacion_id); // id de capacitación base
      this.selectedProgramacionCapacitacionNombre = data.capacitacion?.nombre ?? null;

      this.asistentes = data.asistentes ?? [];
      this.filteredTableItemsAsistencia = [...this.asistentes];
    },
    error: (error) => {
      this.loading = false;
      this.mostrarAlerta("Error", error?.error?.message || "No se pudo cargar la programación", "error");
    },
  });
}

  cargarAsistentesProgramacion(id: number | string): void {
    this.programacionService.getCapacitacionProgramacionByIdAsistentes(id).subscribe({
      next: (data) => {
        this.asistentes = data.asistentes ?? [];
        this.filteredTableItemsAsistencia = [...this.asistentes];
      },
      error: () => this.mostrarAlerta("Error", "No se pudieron actualizar los asistentes", "error"),
    });
  }

  cargarInvitadosProgramacion(id: number | string): void {
    this.loadingInvitados = true;
    this.capacitacionInvitadosService.getCapacitacionInvitadoById(id).subscribe({
      next: (data) => {
        this.loadingInvitados = false;
        this.invitadosProgramacion = data ?? [];
      },
      error: () => {
        this.loadingInvitados = false;
        this.mostrarAlerta("Error", "No se pudieron cargar los invitados.", "error");
      },
    });
  }

  asistioLabel(asistencia: AsistenciaRow): boolean {
    return Number(asistencia?.asistio ?? 0) === 1;
  }

  aprobadoLabel(asistencia: AsistenciaRow): boolean {
    return Number(asistencia?.aprobado ?? 0) === 1;
  }

  // ── Editar asistencia ────────────────────────────────────
  abrirModalEditarAsistente(asistencia: AsistenciaRow, content: TemplateRef<any>): void {
    if (!asistencia?.id) return;

    this.editAsistenciaModalRef = this.modalService.open(content, { centered: true, size: "xl", backdrop: "static" });
    this.editAsistenciaForm = this.formVacioAsistencia();
    this.loading = true;

    this.asistenciaService.getCapacitacionAsistenciaById(asistencia.id).subscribe({
      next: (data: any) => {
        this.loading = false;
        this.editAsistenciaId = Number(data.id);
        this.editAsistenciaForm = {
          capacitacion_id: data.capacitacion_id ?? undefined,
          actividad_id: data.actividad_id ?? undefined,
          cedula: data.cedula ?? undefined,
          nombre: data.nombre ?? "",
          asistio: Number(data.asistio ?? 1),
          aprobado: Number(data.aprobado ?? 0),
          calificacion: data.calificacion != null ? Number(data.calificacion) : null,
          observaciones: data.observaciones ?? "",
          estado: Number(data.estado ?? 1),
        };
      },
      error: (error) => {
        this.loading = false;
        this.editAsistenciaModalRef?.close();
        this.mostrarAlerta("Error", error?.error?.message || "No se pudo cargar la asistencia", "error");
      },
    });
  }
guardarEdicionAsistencia(): void {
  this.editError = "";

  if (!this.editAsistenciaId)
    return this.mostrarAlerta("Error", "No se encontró la asistencia a editar", "error");
  if (!this.editAsistenciaForm.capacitacion_id)
    return this.mostrarAlerta("Campo requerido", "Seleccione la capacitación", "info");
  if (!this.editAsistenciaForm.actividad_id)
    return this.mostrarAlerta("Campo requerido", "Seleccione la actividad/tema al que asistió", "info");
  if (!this.editAsistenciaForm.cedula)
    return this.mostrarAlerta("Campo requerido", "Ingrese la cédula", "info");
  if (!this.editAsistenciaForm.nombre?.trim())
    return this.mostrarAlerta("Campo requerido", "Ingrese el nombre", "info");
  if (!this.selectedProgramacionId)
    return this.mostrarAlerta("Error", "No se encontró la programación seleccionada", "error");

  const payload: CapacitacionAsistenciaView = {
    capacitacion_id: Number(this.editAsistenciaForm.capacitacion_id),
    capacitacion_programacion_id: Number(this.selectedProgramacionId),
    actividad_id: Number(this.editAsistenciaForm.actividad_id),
    cedula: Number(this.editAsistenciaForm.cedula),
    nombre: this.editAsistenciaForm.nombre.trim().toUpperCase(),
    asistio: Number(this.editAsistenciaForm.asistio ?? 1),
    aprobado: Number(this.editAsistenciaForm.aprobado ?? 0),
    calificacion: this.editAsistenciaForm.calificacion ?? null,
    observaciones: this.editAsistenciaForm.observaciones?.trim() ?? "",
    estado: Number(this.editAsistenciaForm.estado ?? 1),
  };

  this.loading = true;
  this.asistenciaService
    .updateCapacitacionAsistencia(this.editAsistenciaId, payload)
    .subscribe({
      next: () => {
        if (this.selectedProgramacionId) {
          this.cargarAsistentesProgramacion(this.selectedProgramacionId);
          this.cargarInvitadosProgramacion(this.selectedProgramacionId);
        }
        this.loading = false;
        this.editAsistenciaModalRef?.close();
        Swal.fire({
          icon: "success",
          title: "Asistencia actualizada",
          text: "El registro se actualizó correctamente.",
        });
      },
      error: (error) => {
        this.loading = false;
        this.mostrarAlerta(
          "No se pudo guardar la asistencia",
          error?.error?.message ||
            "Revisa la información e inténtalo de nuevo.",
          "error",
        );
      },
    });
}

guardarNuevaAsistencia(): void {
  if (!this.newAsistenciaForm.capacitacion_id)
    return this.mostrarAlerta(
      "Campo requerido",
      "Seleccione la capacitación",
      "info",
    );
  if (!this.newAsistenciaForm.actividad_id)
    return this.mostrarAlerta("Campo requerido", "Seleccione la actividad/tema al que asistió", "info");
  if (!this.newAsistenciaForm.cedula)
    return this.mostrarAlerta("Campo requerido", "Ingrese la cédula", "info");
  if (!this.newAsistenciaForm.nombre?.trim())
    return this.mostrarAlerta("Campo requerido", "Ingrese el nombre", "info");
  if (!this.selectedProgramacionId)
    return this.mostrarAlerta("Error", "No se encontró la programación seleccionada", "error");

  const payload: CapacitacionAsistenciaView = {
    capacitacion_id: Number(this.newAsistenciaForm.capacitacion_id),
    capacitacion_programacion_id: Number(this.selectedProgramacionId),
    actividad_id: Number(this.newAsistenciaForm.actividad_id),
    cedula: Number(this.newAsistenciaForm.cedula),
    nombre: this.newAsistenciaForm.nombre.trim().toUpperCase(),
    asistio: Number(this.newAsistenciaForm.asistio ?? 1),
    aprobado: Number(this.newAsistenciaForm.aprobado ?? 0),
    calificacion: this.newAsistenciaForm.calificacion ?? null,
    observaciones: this.newAsistenciaForm.observaciones?.trim() ?? "",
    estado: Number(this.newAsistenciaForm.estado ?? 1),
  };

  this.loading = true;
  this.asistenciaService.createCapacitacion(payload).subscribe({
    next: () => {
      this.loading = false;
      this.cargarAsistentesProgramacion(
        this.selectedProgramacionId as number,
      );
      this.createModalRef?.close();
      this.cargarInvitadosProgramacion(this.selectedProgramacionId as number);
      Swal.fire({
        icon: "success",
        title: "Asistencia creada",
        text: "El registro de asistencia se creó correctamente.",
      });
    },
    error: (error) => {
      this.loading = false;
      this.mostrarAlerta(
        "No se pudo guardar la asistencia",
        error?.error?.message ||
          "Revisa la información e inténtalo de nuevo.",
        "error",
      );
    },
  });
}

  // ── Invitados ─────────────────────────────────────────────
  abrirModalAgregarInvitados(content: TemplateRef<any>): void {
    if (!this.selectedProgramacionId) return;
    this.invitadosSeleccionados = [];
    this.cedulaBuscada = null;
    this.personaNoEncontrada = false;
    this.nuevoInvitadoManual = { cedula: null, nombre: "" };
    this.agregarInvitadosModalRef = this.modalService.open(content, { centered: true, size: "xl", backdrop: "static" });
  }

  guardarNuevosInvitados(): void {
    if (!this.selectedProgramacionId) return this.mostrarAlerta("Error", "No se encontró la programación seleccionada", "error");
    if (!this.invitadosSeleccionados?.length) return this.mostrarAlerta("Campo requerido", "Seleccione al menos un invitado", "info");

    const programacionId = this.selectedProgramacionId;
    const peticiones = this.invitadosSeleccionados.map((inv) =>
      this.capacitacionInvitadosService.createCapacitacionInvitado({
        capacitacion_programacion_id: Number(programacionId),
        propietario_id: inv.cedula,
        nombre: inv.nombre,
        estado: 1,
      }),
    );

    this.loading = true;
    forkJoin(peticiones).subscribe({
      next: () => {
        this.loading = false;
        this.agregarInvitadosModalRef?.close();
        Swal.fire({ icon: "success", title: "Invitados agregados", text: "Los invitados se agregaron correctamente." });
        this.cargarInvitadosProgramacion(programacionId);
        this.invitadosSeleccionados = [];
      },
      error: (error) => {
        this.loading = false;
        this.cargarInvitadosProgramacion(programacionId);
        this.mostrarAlerta("No se pudieron agregar algunos invitados", error?.error?.message || "Revisa la información e inténtalo de nuevo.", "error");
      },
    });
  }

  quitarInvitado(invitado: any): void {
    if (!invitado?.id) return;
    Swal.fire({
      title: "¿Desea quitar este invitado?",
      text: "El invitado dejará de estar asociado a esta programación.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, quitar",
      cancelButtonText: "Cancelar",
    }).then((result: any) => {
      if (!result.isConfirmed) return;
      this.capacitacionInvitadosService.inactivar(invitado.id).subscribe({
        next: () => {
          Swal.fire("Invitado removido", "El invitado fue removido correctamente.", "success");
          if (this.selectedProgramacionId) this.cargarInvitadosProgramacion(this.selectedProgramacionId);
        },
        error: () => this.mostrarAlerta("Error", "No se pudo quitar el invitado.", "error"),
      });
    });
  }

  buscarInvitadoPorCedula(): void {
    const cedula = String(this.cedulaBuscada ?? "").trim();
    if (!cedula) return;

    if (this.invitadosSeleccionados.some((inv) => String(inv.cedula) === cedula)) {
      return this.mostrarAlerta("Aviso", "Esta persona ya fue agregada", "info");
    }

    this.personaNoEncontrada = false;
    this.buscandoInvitado = true;

    this.propietarioService.getCedulaPesv(cedula).subscribe({
      next: (res: any) => {
        this.buscandoInvitado = false;
        const propietario = res?.propietario;

        if (!propietario?.cedula) return this.activarAgregarManual(cedula);

        const nombreCompleto = [propietario.primernombre, propietario.segundonombre, propietario.papellidos, propietario.seapellidos]
          .map((p: string) => (p ?? "").trim())
          .filter((p: string) => !!p)
          .join(" ");

        this.invitadosSeleccionados.push({ cedula: propietario.cedula, nombre: nombreCompleto });
        this.cedulaBuscada = null;
      },
      error: () => {
        this.buscandoInvitado = false;
        this.activarAgregarManual(cedula);
      },
    });
  }

  private activarAgregarManual(cedula: string): void {
    this.personaNoEncontrada = true;
    this.nuevoInvitadoManual = { cedula, nombre: "" };
  }

  agregarInvitadoManual(): void {
    const cedula = String(this.nuevoInvitadoManual.cedula ?? "").trim();
    const nombre = this.nuevoInvitadoManual.nombre?.trim();

    if (!cedula) return this.mostrarAlerta("Campo requerido", "Ingrese la cédula", "info");
    if (!nombre) return this.mostrarAlerta("Campo requerido", "Ingrese el nombre", "info");
    if (this.invitadosSeleccionados.some((inv) => String(inv.cedula) === cedula)) {
      return this.mostrarAlerta("Aviso", "Esta persona ya fue agregada", "info");
    }

    this.invitadosSeleccionados.push({ cedula, nombre: nombre.toUpperCase() });
    this.personaNoEncontrada = false;
    this.cedulaBuscada = null;
    this.nuevoInvitadoManual = { cedula: null, nombre: "" };
  }

  cancelarAgregarManual(): void {
    this.personaNoEncontrada = false;
    this.nuevoInvitadoManual = { cedula: null, nombre: "" };
  }

  eliminarInvitado(cedula: number | string): void {
    this.invitadosSeleccionados = this.invitadosSeleccionados.filter((inv) => String(inv.cedula) !== String(cedula));
  }

  // ── Exportar ──────────────────────────────────────────────
  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }

  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  private mostrarAlerta(titulo: string, mensaje: string, icono: any): void {
    Swal.fire({ title: titulo, text: mensaje, icon: icono, confirmButtonColor: "#3085d6" });
  }

abrirModalAsistente(asistencia: AsistenciaRow | null, content: TemplateRef<any>): void {
  this.modoEdicion = !!asistencia;
  this.asistenciaIdParaEditar = asistencia?.id ?? null;

  this.asistenciaForm = this.formVacioAsistencia();

  // Forzar capacitación de la programación
  this.asistenciaForm.capacitacion_id = this.selectedProgramacionCapacitacionRefId ?? undefined;

  if (this.modoEdicion && asistencia?.id) {
    this.loading = true;
    this.asistenciaService.getCapacitacionAsistenciaById(asistencia.id).subscribe({
      next: (data: any) => {
        this.loading = false;
        this.asistenciaForm = {
          capacitacion_id: data.capacitacion_id ?? this.selectedProgramacionCapacitacionRefId ?? undefined,
          actividad_id: data.actividad_id ?? undefined,
          cedula: data.cedula ?? undefined,
          nombre: data.nombre ?? "",
          asistio: Number(data.asistio ?? 1),
          aprobado: Number(data.aprobado ?? 0),
          calificacion: data.calificacion != null ? Number(data.calificacion) : null,
          observaciones: data.observaciones ?? "",
          estado: Number(data.estado ?? 1),
        };
      },
      error: (error) => {
        this.loading = false;
        this.mostrarAlerta("Error", error?.error?.message || "No se pudo cargar la asistencia", "error");
      },
    });
  }

  this.modalService.open(content, { centered: true, size: "xl", backdrop: "static" });
}

guardarAsistencia(): void {
  if (!this.asistenciaForm.capacitacion_id) {
    return this.mostrarAlerta("Campo requerido", "Seleccione la capacitación", "info");
  }
  if (!this.asistenciaForm.actividad_id) {
    return this.mostrarAlerta("Campo requerido", "Seleccione la actividad/tema al que asistió", "info");
  }
  if (!this.asistenciaForm.cedula) {
    return this.mostrarAlerta("Campo requerido", "Ingrese la cédula", "info");
  }
  if (!this.asistenciaForm.nombre?.trim()) {
    return this.mostrarAlerta("Campo requerido", "Ingrese el nombre", "info");
  }
  if (!this.selectedProgramacionId) {
    return this.mostrarAlerta("Error", "No se encontró la programación seleccionada", "error");
  }

  const payload: CapacitacionAsistenciaView = {
    capacitacion_id: Number(this.asistenciaForm.capacitacion_id),
    capacitacion_programacion_id: Number(this.selectedProgramacionId),
    actividad_id: Number(this.asistenciaForm.actividad_id),
    cedula: Number(this.asistenciaForm.cedula),
    nombre: this.asistenciaForm.nombre.trim().toUpperCase(),
    asistio: Number(this.asistenciaForm.asistio ?? 1),
    aprobado: Number(this.asistenciaForm.aprobado ?? 0),
    calificacion: this.asistenciaForm.calificacion ?? null,
    observaciones: this.asistenciaForm.observaciones?.trim() ?? "",
    estado: Number(this.asistenciaForm.estado ?? 1),
  };

  this.loading = true;

  if (this.modoEdicion && this.asistenciaIdParaEditar) {
    this.asistenciaService.updateCapacitacionAsistencia(this.asistenciaIdParaEditar, payload).subscribe({
      next: () => {
        this.loading = false;
        if (this.selectedProgramacionId) {
          this.cargarAsistentesProgramacion(this.selectedProgramacionId);
          this.cargarInvitadosProgramacion(this.selectedProgramacionId);
        }
        this.modalService.dismissAll();
        Swal.fire({ icon: "success", title: "Asistencia actualizada", text: "El registro se actualizó correctamente." });
      },
      error: (error) => {
        this.loading = false;
        this.mostrarAlerta("No se pudo guardar la asistencia", error?.error?.message || "Revisa la información e inténtalo de nuevo.", "error");
      },
    });
  } else {
    this.asistenciaService.createCapacitacion(payload).subscribe({
      next: () => {
        this.loading = false;
        if (this.selectedProgramacionId) {
          this.cargarAsistentesProgramacion(this.selectedProgramacionId);
          this.cargarInvitadosProgramacion(this.selectedProgramacionId);
        }
        this.modalService.dismissAll();
        Swal.fire({ icon: "success", title: "Asistencia creada", text: "El registro de asistencia se creó correctamente." });
      },
      error: (error) => {
        this.loading = false;
        this.mostrarAlerta("No se pudo guardar la asistencia", error?.error?.message || "Revisa la información e inténtalo de nuevo.", "error");
      },
    });
  }
}
}